/**
 * A template's organization hierarchy (PR Template, Supplier Onboarding) -- several Entities, Business
 * Units, Divisions and Departments -- and a submitted form's, mirroring the
 * backend's `HierarchyScopeSelection` / `HierarchyScopeResponse`
 * (`docs/api/TEMPLATE-API.md`, "Hierarchy mapping").
 */

/** The four levels, top down, as the options endpoint names them. */
export type HierarchyLevel = 'entities' | 'business-units' | 'divisions' | 'departments';

export const HIERARCHY_LEVELS: readonly HierarchyLevel[] = ['entities', 'business-units', 'divisions', 'departments'];

/** The selection: ids per level, in the order chosen. What a save and a submit send as `scope`. */
export interface HierarchyScope {
  legalEntityIds: number[];
  businessUnitIds: number[];
  divisionIds: number[];
  departmentIds: number[];
}

export const EMPTY_SCOPE: HierarchyScope = {
  legalEntityIds: [],
  businessUnitIds: [],
  divisionIds: [],
  departmentIds: [],
};

/** The selection's field for a level -- also the request parameter and the error key (`scope.<field>`). */
export const SCOPE_FIELD: Readonly<Record<HierarchyLevel, keyof HierarchyScope>> = {
  entities: 'legalEntityIds',
  'business-units': 'businessUnitIds',
  divisions: 'divisionIds',
  departments: 'departmentIds',
};

/** What each level is called on screen. */
export const HIERARCHY_LEVEL_LABEL: Readonly<Record<HierarchyLevel, string>> = {
  entities: 'Entity',
  'business-units': 'Business Unit',
  divisions: 'Division',
  departments: 'Department',
};

/** One option: the server's label is `CODE + Name (Parent Code)`. */
export interface HierarchyNode {
  id: number;
  code?: string | null;
  name: string;
  parentId?: number | null;
  parentCode?: string | null;
  label: string;
}

/** A stored mapping as read back: every value labelled, level by level. */
export interface HierarchyScopeView {
  legalEntities: HierarchyNode[];
  businessUnits: HierarchyNode[];
  divisions: HierarchyNode[];
  departments: HierarchyNode[];
}

const VIEW_FIELD: Readonly<Record<HierarchyLevel, keyof HierarchyScopeView>> = {
  entities: 'legalEntities',
  'business-units': 'businessUnits',
  divisions: 'divisions',
  departments: 'departments',
};

/**
 * What a selection still needs, by level: one sentence per chosen parent that has children none of which is
 * chosen -- "Please select a Division for Business Unit BU-001 – Procurement." Empty when complete.
 */
export type HierarchyIssues = Partial<Record<HierarchyLevel, string[]>>;

export function hasIssues(issues: HierarchyIssues): boolean {
  return Object.values(issues).some((messages) => messages.length > 0);
}

/** The level a level's options are children of; none for Entity. */
export function parentLevel(level: HierarchyLevel): HierarchyLevel | null {
  const index = HIERARCHY_LEVELS.indexOf(level);
  return index > 0 ? (HIERARCHY_LEVELS[index - 1] ?? null) : null;
}

/** A read-back mapping as a selection, for Edit to restore. */
export function scopeFromView(view: HierarchyScopeView | null | undefined): HierarchyScope {
  if (!view) return EMPTY_SCOPE;
  return {
    legalEntityIds: view.legalEntities.map((node) => node.id),
    businessUnitIds: view.businessUnits.map((node) => node.id),
    divisionIds: view.divisions.map((node) => node.id),
    departmentIds: view.departments.map((node) => node.id),
  };
}

/** A level's labelled values in a read-back mapping. */
export function viewNodes(view: HierarchyScopeView | null | undefined, level: HierarchyLevel): HierarchyNode[] {
  return view ? view[VIEW_FIELD[level]] : [];
}

export function isScopeEmpty(scope: HierarchyScope): boolean {
  return HIERARCHY_LEVELS.every((level) => scope[SCOPE_FIELD[level]].length === 0);
}

/**
 * The selection as listing query parameters: each chosen level as a
 * comma-separated id list (`legalEntityIds=1,2`), which the endpoints bind to a
 * list. Empty levels are left out -- they filter nothing.
 */
export function scopeQuery(scope: HierarchyScope): Record<string, string> {
  const params: Record<string, string> = {};
  for (const level of HIERARCHY_LEVELS) {
    const ids = scope[SCOPE_FIELD[level]];
    if (ids.length > 0) params[SCOPE_FIELD[level]] = ids.join(',');
  }
  return params;
}

/** The reverse of {@link scopeQuery}, for a listing client (and its mock) reading a query's extras. */
export function scopeOfQuery(query: object): HierarchyScope {
  const read = (key: keyof HierarchyScope): number[] => {
    const raw = (query as Record<string, unknown>)[key];
    if (typeof raw !== 'string' && typeof raw !== 'number') return [];
    return String(raw)
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0);
  };
  return {
    legalEntityIds: read('legalEntityIds'),
    businessUnitIds: read('businessUnitIds'),
    divisionIds: read('divisionIds'),
    departmentIds: read('departmentIds'),
  };
}

/**
 * The listings' matching rule, as the server applies it: at every level the
 * filter chooses values for, the template is not mapped there or is mapped to
 * one of them. Used by the mocks; the live filter is the server's.
 */
export function scopeApplies(mapped: HierarchyScope, filter: HierarchyScope): boolean {
  return HIERARCHY_LEVELS.every((level) => {
    const chosen = filter[SCOPE_FIELD[level]];
    const allowed = mapped[SCOPE_FIELD[level]];
    return chosen.length === 0 || allowed.length === 0 || chosen.some((id) => allowed.includes(id));
  });
}

/**
 * The active-template listings' stricter rule, as the server's `mappedTo`: with a filter, the template must be
 * mapped somewhere and apply to it -- a template mapped nowhere belongs to no hierarchy. No filter, no condition.
 */
export function scopeMatches(mapped: HierarchyScope, filter: HierarchyScope): boolean {
  if (isScopeEmpty(filter)) return true;
  return !isScopeEmpty(mapped) && scopeApplies(mapped, filter);
}

/** The `scope.<field>` messages of a refused save or submit, by level. */
export function scopeErrorsOf(fieldErrors: Readonly<Record<string, string>>): Partial<Record<HierarchyLevel, string>> {
  const out: Partial<Record<HierarchyLevel, string>> = {};
  for (const level of HIERARCHY_LEVELS) {
    const message = fieldErrors[`scope.${SCOPE_FIELD[level]}`];
    if (message) out[level] = message;
  }
  return out;
}